import React, { useState, useEffect } from "react";
import { Row, Col, message, Modal, Form, InputNumber, DatePicker, Input, Select, Divider, Radio, Space, Button, Tooltip } from "antd";
import { FormOutlined, HistoryOutlined, SyncOutlined, FundProjectionScreenOutlined } from "@ant-design/icons";
import { 
    AircraftConfigForm, 
    AircraftConfigList, 
    DataEntryHistory,
    TimelineAircraftConfig,
    MaintenanceEventForm,
    MaintenanceEventList,
} from "./components";
import apiService, { flightOperationFlightService } from "../../api/enhancedApi";
import { aircraftApi } from "../../api/masterDataApi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./ScreenControlPage.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

// 视图类型常量
const VIEW_TYPE = {
    CONFIG: "config",
    HISTORY: "history",
};

/**
 * 运营大屏配置管理页面
 * 功能：配置飞机运行数据、定检维护事件，监控展示页连接状态
 */
const ScreenControlPage = () => {
    const [loading, setLoading] = useState(false);
    const [activeView, setActiveView] = useState(VIEW_TYPE.CONFIG);
    const [aircraftConfigs, setAircraftConfigs] = useState([]);
    const [historyData, setHistoryData] = useState([]);
    const [timelineAircraft, setTimelineAircraft] = useState([]);
    const [allAircraftList, setAllAircraftList] = useState([]);
    const [maintenanceEvents, setMaintenanceEvents] = useState([]);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editMaintenanceModalVisible, setEditMaintenanceModalVisible] = useState(false);
    const [editingMaintenance, setEditingMaintenance] = useState(null);
    const [editFlights, setEditFlights] = useState([]);
    const [editFlightsLoading, setEditFlightsLoading] = useState(false);
    const [editPrevFlightIndex, setEditPrevFlightIndex] = useState(-1);
    const [editForm] = Form.useForm();
    const [editMaintenanceForm] = Form.useForm();

    // 加载飞机配置列表
    const loadAircraftConfigs = async () => {
        try {
            setLoading(true);
            const response = await apiService.screen.getAircraftConfigs();
            setAircraftConfigs(response.data || []);
        } catch (error) {
            console.error("加载飞机配置失败:", error);
            message.error("加载飞机配置失败");
        } finally {
            setLoading(false);
        }
    };

    // 加载历史记录
    const loadHistory = async () => {
        try {
            setLoading(true);
            const response = await apiService.screen.getDataEntryHistory();
            setHistoryData(response.data || []);
        } catch (error) {
            console.error("加载历史记录失败:", error);
            message.error("加载历史记录失败");
        } finally {
            setLoading(false);
        }
    };

    // 加载时间轴飞机配置
    const loadTimelineAircraft = async () => {
        try {
            setLoading(true);
            const response = await apiService.screen.getTimelineAircraft();
            setTimelineAircraft(response.data || []);
        } catch (error) {
            console.error("加载时间轴飞机配置失败:", error);
            message.error("加载时间轴飞机配置失败");
        } finally {
            setLoading(false);
        }
    };

    // 加载所有飞机列表（用于下拉选择）
    const loadAllAircraft = async () => {
        try {
            const response = await aircraftApi.getAircraftList({ page_size: 500 });
            // 提取飞机注册号列表
            const aircraftList = response.data?.items?.map(item => item.registration) || [];
            setAllAircraftList(aircraftList);
        } catch (error) {
            console.error("加载飞机列表失败:", error);
            // 不显示错误提示，使用静默失败
        }
    };

    // 加载定检维护事件列表
    const loadMaintenanceEvents = async () => {
        try {
            setLoading(true);
            const response = await apiService.screen.getMaintenanceEvents();
            setMaintenanceEvents(response.data || []);
        } catch (error) {
            console.error("加载维护事件失败:", error);
            message.error("加载维护事件失败");
        } finally {
            setLoading(false);
        }
    };

    // 刷新当前视图数据
    const handleRefresh = () => {
        if (activeView === VIEW_TYPE.CONFIG) {
            loadAircraftConfigs();
            loadTimelineAircraft();
            loadMaintenanceEvents();
        } else if (activeView === VIEW_TYPE.HISTORY) {
            loadHistory();
        }
    };

    // 页面加载时获取数据
    useEffect(() => {
        loadAircraftConfigs();
        loadTimelineAircraft();
        loadAllAircraft();
        loadMaintenanceEvents();
    }, []);

    // 切换视图时加载对应数据
    useEffect(() => {
        if (activeView === VIEW_TYPE.HISTORY && historyData.length === 0) {
            loadHistory();
        }
    }, [activeView]);

    // 创建新的飞机配置
    const handleCreate = async (data) => {
        try {
            setLoading(true);
            const response = await apiService.screen.createAircraftConfig(data);
            
            if (response.data?.success) {
                message.success("飞机配置创建成功");
                await loadAircraftConfigs(); // 重新加载列表
                // 如果在历史记录视图，也刷新历史
                if (activeView === VIEW_TYPE.HISTORY) {
                    await loadHistory();
                }
            } else {
                throw new Error(response.data?.message || "创建失败");
            }
        } catch (error) {
            console.error("创建飞机配置失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "创建失败";
            message.error(errorMsg);
            throw error; // 重新抛出错误，让表单组件处理
        } finally {
            setLoading(false);
        }
    };

    // 打开编辑模态框
    const handleEdit = (record) => {
        setEditingRecord(record);
        editForm.setFieldsValue({
            planned_hours: record.planned_hours,
            next_inspection_date: dayjs(record.next_inspection_date),
        });
        setEditModalVisible(true);
    };

    // 提交编辑
    const handleEditSubmit = async () => {
        try {
            const values = await editForm.validateFields();
            setLoading(true);

            const updateData = {
                planned_hours: values.planned_hours,
                next_inspection_date: values.next_inspection_date.format("YYYY-MM-DD"),
            };

            const response = await apiService.screen.updateAircraftConfig({
                aircraftRegistration: editingRecord.aircraft_registration,
                data: updateData,
            });

            if (response.data?.success) {
                message.success("飞机配置更新成功");
                setEditModalVisible(false);
                setEditingRecord(null);
                editForm.resetFields();
                await loadAircraftConfigs(); // 重新加载列表
                // 如果在历史记录视图，也刷新历史
                if (activeView === VIEW_TYPE.HISTORY) {
                    await loadHistory();
                }
            } else {
                throw new Error(response.data?.message || "更新失败");
            }
        } catch (error) {
            if (error.errorFields) {
                message.error("请检查表单填写是否完整");
            } else {
                console.error("更新飞机配置失败:", error);
                const errorMsg = error.response?.data?.detail || error.message || "更新失败";
                message.error(errorMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    // 删除飞机配置
    const handleDelete = async (record) => {
        try {
            setLoading(true);
            const response = await apiService.screen.deleteAircraftConfig(
                record.aircraft_registration
            );

            if (response.data?.success) {
                message.success("飞机配置删除成功");
                await loadAircraftConfigs(); // 重新加载列表
                // 如果在历史记录视图，也刷新历史
                if (activeView === VIEW_TYPE.HISTORY) {
                    await loadHistory();
                }
            } else {
                throw new Error(response.data?.message || "删除失败");
            }
        } catch (error) {
            console.error("删除飞机配置失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "删除失败";
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // ==================== 定检维护事件操作 ====================

    const getFlightKey = (flight) => {
        const flightDate = dayjs(flight.flight_date).format("YYYY-MM-DD");
        return `${flight.id}|${flight.flight_number}|${flightDate}`;
    };

    const loadEditFlights = async (aircraftReg, referenceDate = dayjs()) => {
        try {
            setEditFlightsLoading(true);
            const reference = dayjs(referenceDate);
            const startDate = reference.subtract(30, "day").format("YYYY-MM-DD");
            const endDate = reference.add(30, "day").format("YYYY-MM-DD");
            const response = await flightOperationFlightService.getFlightOperations({
                aircraft_reg: aircraftReg,
                start_date: startDate,
                end_date: endDate,
                page_size: 200,
            });
            const EXCLUDED_STATUS = ["CANCELLED", "PLAN_CANCELLED", "REPLACED"];
            const flightList = (response.data?.items || []).filter(
                (f) => !EXCLUDED_STATUS.includes(f.status)
            );
            flightList.sort((a, b) => dayjs(a.std).diff(dayjs(b.std)));
            setEditFlights(flightList);
        } catch (error) {
            console.error("加载航班列表失败:", error);
            message.error("加载航班列表失败");
            setEditFlights([]);
        } finally {
            setEditFlightsLoading(false);
        }
    };

    const handleEditPrevFlightChange = (value) => {
        if (!value) {
            setEditPrevFlightIndex(-1);
            editMaintenanceForm.setFieldsValue({ next_flight_key: undefined });
            return;
        }
        const [flightId] = value.split("|");
        const flightIndex = editFlights.findIndex((f) => String(f.id) === flightId);

        setEditPrevFlightIndex(flightIndex);
        if (flightIndex >= 0 && flightIndex < editFlights.length - 1) {
            const nextFlight = editFlights[flightIndex + 1];
            editMaintenanceForm.setFieldsValue({
                next_flight_key: getFlightKey(nextFlight),
            });
        } else {
            editMaintenanceForm.setFieldsValue({ next_flight_key: undefined });
        }
    };

    const renderEditFlightOption = (flight) => {
        const flightDate = dayjs(flight.flight_date).format("YYYY-MM-DD");
        const stdStr = flight.std ? dayjs(flight.std).tz("Asia/Shanghai").format("MM-DD HH:mm") : "--";
        const staStr = flight.sta ? dayjs(flight.sta).tz("Asia/Shanghai").format("MM-DD HH:mm") : "--";
        const key = `${flight.id}|${flight.flight_number}|${flightDate}`;
        return (
            <Option key={key} value={key}>
                {flight.flight_number} | {flight.departure_airport}→{flight.arrival_airport} | {stdStr} → {staStr}
            </Option>
        );
    };

    const resetEditFlightState = () => {
        setEditFlights([]);
        setEditFlightsLoading(false);
        setEditPrevFlightIndex(-1);
    };
    const handleCreateMaintenanceEvent = async (data) => {
        try {
            setLoading(true);
            const response = await apiService.screen.createMaintenanceEvent(data);
            
            if (response.data?.success) {
                message.success("维护事件创建成功");
                await loadMaintenanceEvents();
            } else {
                throw new Error(response.data?.message || "创建失败");
            }
        } catch (error) {
            console.error("创建维护事件失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "创建失败";
            message.error(errorMsg);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const handleEditMaintenanceEvent = (record) => {
        setEditingMaintenance(record);
        resetEditFlightState();

        const status = record.planning_status || "CONFIRMED";
        editMaintenanceForm.setFieldsValue({
            planning_status: status,
            expected_start_date: record.expected_start_date ? dayjs(record.expected_start_date) : undefined,
            event_type: record.event_type,
            title: record.title,
            description: record.description,
            prev_flight_key: undefined,
            next_flight_key: undefined,
        });

        if (status === "PENDING") {
            loadEditFlights(
                record.aircraft_registration,
                record.expected_start_date || dayjs(),
            );
        }

        setEditMaintenanceModalVisible(true);
    };

    const handleEditMaintenanceSubmit = async () => {
        try {
            const values = await editMaintenanceForm.validateFields();

            const wasConfirmed = (editingMaintenance.planning_status || "CONFIRMED") === "CONFIRMED";
            const nowConfirmed = values.planning_status === "CONFIRMED";
            const needFlights = nowConfirmed && !wasConfirmed;

            if (needFlights) {
                if (!values.prev_flight_key || !values.next_flight_key) {
                    message.error("切换为已确认状态时，必须选择前后航班");
                    return;
                }
            }

            setLoading(true);

            const updateData = {
                planning_status: values.planning_status,
                event_type: values.event_type,
                title: values.title || null,
                description: values.description || null,
            };

            if (values.planning_status === "PENDING") {
                updateData.expected_start_date = values.expected_start_date
                    ? values.expected_start_date.format("YYYY-MM-DD")
                    : null;
            }

            if (needFlights) {
                const [, prevFlightNumber, prevFlightDate] = values.prev_flight_key.split("|");
                const [, nextFlightNumber, nextFlightDate] = values.next_flight_key.split("|");
                updateData.prev_flight_number = prevFlightNumber;
                updateData.prev_flight_date = prevFlightDate;
                updateData.next_flight_number = nextFlightNumber;
                updateData.next_flight_date = nextFlightDate;
            }

            const response = await apiService.screen.updateMaintenanceEvent({
                id: editingMaintenance.id,
                data: updateData,
            });

            if (response.data?.success) {
                message.success("维护事件更新成功");
                setEditMaintenanceModalVisible(false);
                setEditingMaintenance(null);
                editMaintenanceForm.resetFields();
                resetEditFlightState();
                await loadMaintenanceEvents();
            } else {
                throw new Error(response.data?.message || "更新失败");
            }
        } catch (error) {
            if (error.errorFields) {
                message.error("请检查表单填写是否完整");
            } else {
                console.error("更新维护事件失败:", error);
                const errorMsg = error.response?.data?.detail || error.message || "更新失败";
                message.error(errorMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    // 删除维护事件
    const handleDeleteMaintenanceEvent = async (record) => {
        try {
            setLoading(true);
            const response = await apiService.screen.deleteMaintenanceEvent(record.id);

            if (response.data?.success) {
                message.success("维护事件删除成功");
                await loadMaintenanceEvents();
            } else {
                throw new Error(response.data?.message || "删除失败");
            }
        } catch (error) {
            console.error("删除维护事件失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "删除失败";
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // ==================== 时间轴飞机配置操作 ====================

    // 添加飞机到时间轴
    const handleAddTimelineAircraft = async (data) => {
        try {
            setLoading(true);
            const response = await apiService.screen.createTimelineAircraft(data);
            
            if (response.data?.success) {
                message.success("飞机已添加到大屏");
                await loadTimelineAircraft();
            } else {
                throw new Error(response.data?.message || "添加失败");
            }
        } catch (error) {
            console.error("添加时间轴飞机失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "添加失败";
            message.error(errorMsg);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // 删除时间轴飞机
    const handleDeleteTimelineAircraft = async (record) => {
        try {
            setLoading(true);
            const response = await apiService.screen.deleteTimelineAircraft(record.aircraft_registration);
            
            if (response.data?.success) {
                message.success("已从大屏移除");
                await loadTimelineAircraft();
            } else {
                throw new Error(response.data?.message || "删除失败");
            }
        } catch (error) {
            console.error("删除时间轴飞机失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "删除失败";
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // 切换时间轴飞机激活状态
    const handleToggleTimelineAircraft = async (record, checked) => {
        try {
            setLoading(true);
            const response = await apiService.screen.updateTimelineAircraft({
                aircraftRegistration: record.aircraft_registration,
                data: { is_active: checked }
            });
            
            if (response.data?.success) {
                message.success(checked ? "已激活" : "已停用");
                await loadTimelineAircraft();
            } else {
                throw new Error(response.data?.message || "更新失败");
            }
        } catch (error) {
            console.error("切换时间轴飞机状态失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "更新失败";
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // 上移时间轴飞机
    const handleMoveUpTimelineAircraft = async (record, index) => {
        if (index === 0) return;
        
        try {
            setLoading(true);
            const newList = [...timelineAircraft];
            
            // 交换位置
            [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
            
            // 更新display_order
            const reorderItems = newList.map((item, idx) => ({
                aircraft_registration: item.aircraft_registration,
                display_order: idx + 1
            }));
            
            const response = await apiService.screen.reorderTimelineAircraft({
                items: reorderItems
            });
            
            if (response.data?.success) {
                await loadTimelineAircraft();
            } else {
                throw new Error(response.data?.message || "更新顺序失败");
            }
        } catch (error) {
            console.error("上移飞机失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "操作失败";
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // 下移时间轴飞机
    const handleMoveDownTimelineAircraft = async (record, index) => {
        if (index === timelineAircraft.length - 1) return;
        
        try {
            setLoading(true);
            const newList = [...timelineAircraft];
            
            // 交换位置
            [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
            
            // 更新display_order
            const reorderItems = newList.map((item, idx) => ({
                aircraft_registration: item.aircraft_registration,
                display_order: idx + 1
            }));
            
            const response = await apiService.screen.reorderTimelineAircraft({
                items: reorderItems
            });
            
            if (response.data?.success) {
                await loadTimelineAircraft();
            } else {
                throw new Error(response.data?.message || "更新顺序失败");
            }
        } catch (error) {
            console.error("下移飞机失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "操作失败";
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // 渲染配置录入视图
    const renderConfigView = () => (
        <>
            {/* 飞行时间轴飞机选择区域 */}
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <TimelineAircraftConfig
                        dataSource={timelineAircraft}
                        allAircraftList={allAircraftList}
                        loading={loading}
                        onAdd={handleAddTimelineAircraft}
                        onDelete={handleDeleteTimelineAircraft}
                        onToggleActive={handleToggleTimelineAircraft}
                        onMoveUp={handleMoveUpTimelineAircraft}
                        onMoveDown={handleMoveDownTimelineAircraft}
                    />
                </Col>
            </Row>

            {/* 飞机配置区域（已隐藏：planned_hours/next_inspection_date 字段未被大屏使用） */}

            <Divider style={{ margin: "32px 0" }} />

            {/* 定检维护事件区域 */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={8}>
                    <MaintenanceEventForm 
                        onSuccess={handleCreateMaintenanceEvent}
                        loading={loading}
                    />
                </Col>
                
                <Col xs={24} lg={16}>
                    <MaintenanceEventList
                        dataSource={maintenanceEvents}
                        loading={loading}
                        onEdit={handleEditMaintenanceEvent}
                        onDelete={handleDeleteMaintenanceEvent}
                    />
                </Col>
            </Row>
        </>
    );

    // 渲染历史记录视图
    const renderHistoryView = () => (
        <DataEntryHistory
            dataSource={historyData}
            loading={loading}
            onRefresh={loadHistory}
        />
    );

    return (
        <div className="page-container">
            {/* 页面头部 */}
            <div className="page-header-compact">
                <div className="page-title-compact">
                    运营大屏控制
                    <span style={{ color: "#999", fontSize: "12px", marginLeft: "8px" }}>v1.0</span>
                </div>
                <div className="page-action-compact">
                    <Space size="middle" wrap>
                        {/* 视图切换 */}
                        <Radio.Group
                            value={activeView}
                            onChange={(e) => setActiveView(e.target.value)}
                            buttonStyle="solid"
                            optionType="button"
                        >
                            <Tooltip title="配置时间轴飞机和维护事件">
                                <Radio.Button value={VIEW_TYPE.CONFIG}>
                                    <FormOutlined /> 配置录入
                                </Radio.Button>
                            </Tooltip>
                            <Tooltip title="查看数据修改历史记录">
                                <Radio.Button value={VIEW_TYPE.HISTORY}>
                                    <HistoryOutlined /> 历史记录
                                </Radio.Button>
                            </Tooltip>
                        </Radio.Group>

                        {/* 刷新按钮 */}
                        <Tooltip title="刷新数据">
                            <Button
                                icon={<SyncOutlined spin={loading} />}
                                onClick={handleRefresh}
                                loading={loading}
                            >
                                刷新
                            </Button>
                        </Tooltip>

                        {/* 打开大屏按钮 */}
                        <Tooltip title="在新窗口打开左侧大屏">
                            <Button
                                type="primary"
                                icon={<FundProjectionScreenOutlined />}
                                onClick={() => window.open("/operation/screen-left", "_blank")}
                            >
                                打开左侧大屏
                            </Button>
                        </Tooltip>
                        <Tooltip title="在新窗口打开右侧大屏">
                            <Button
                                type="primary"
                                icon={<FundProjectionScreenOutlined />}
                                onClick={() => window.open("/operation/screen-right", "_blank")}
                            >
                                打开右侧大屏
                            </Button>
                        </Tooltip>
                    </Space>
                </div>
            </div>

            {/* 内容区域 */}
            <div style={{ padding: "16px 24px" }}>
                {activeView === VIEW_TYPE.CONFIG ? renderConfigView() : renderHistoryView()}
            </div>

            {/* 编辑飞机配置模态框 */}
            <Modal
                title={`编辑飞机配置 - ${editingRecord?.aircraft_registration}`}
                open={editModalVisible}
                onOk={handleEditSubmit}
                onCancel={() => {
                    setEditModalVisible(false);
                    setEditingRecord(null);
                    editForm.resetFields();
                }}
                confirmLoading={loading}
                okText="保存"
                cancelText="取消"
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    autoComplete="off"
                >
                    <Form.Item
                        name="planned_hours"
                        label="年计划运行小时"
                        rules={[
                            { required: true, message: "请输入年计划运行小时" },
                            { type: "number", min: 1, message: "必须大于0" },
                        ]}
                    >
                        <InputNumber
                            placeholder="例如：4815"
                            min={1}
                            max={99999}
                            style={{ width: "100%" }}
                            addonAfter="小时"
                        />
                    </Form.Item>

                    <Form.Item
                        name="next_inspection_date"
                        label="下一次检查日期"
                        rules={[
                            { required: true, message: "请选择下一次检查日期" },
                        ]}
                    >
                        <DatePicker
                            placeholder="选择日期"
                            style={{ width: "100%" }}
                            format="YYYY-MM-DD"
                            disabledDate={(current) => {
                                return current && current < dayjs().startOf("day");
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 编辑维护事件模态框 */}
            <Modal
                title={`编辑维护事件 - ${editingMaintenance?.aircraft_registration}`}
                open={editMaintenanceModalVisible}
                onOk={handleEditMaintenanceSubmit}
                onCancel={() => {
                    setEditMaintenanceModalVisible(false);
                    setEditingMaintenance(null);
                    editMaintenanceForm.resetFields();
                    resetEditFlightState();
                }}
                confirmLoading={loading}
                okText="保存"
                cancelText="取消"
                width={560}
            >
                <Form
                    form={editMaintenanceForm}
                    layout="vertical"
                    autoComplete="off"
                >
                    <Form.Item
                        name="planning_status"
                        label="计划状态"
                    >
                        <Radio.Group buttonStyle="solid" optionType="button" size="small">
                            <Radio.Button value="CONFIRMED">已确认</Radio.Button>
                            <Radio.Button value="PENDING">计划待定</Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.planning_status !== cur.planning_status}>
                        {({ getFieldValue }) => {
                            const status = getFieldValue("planning_status");
                            const alreadyHasFlights = !!editingMaintenance?.prev_flight_number;

                            if (status === "PENDING") {
                                return (
                                    <Form.Item
                                        name="expected_start_date"
                                        label="预计维护日期"
                                        rules={[{ required: true, message: "请选择预计维护日期" }]}
                                    >
                                        <DatePicker
                                            placeholder="选择预计开始日期"
                                            style={{ width: "100%" }}
                                            format="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                );
                            }

                            if (status === "CONFIRMED" && alreadyHasFlights) {
                                return (
                                    <div style={{ marginBottom: 16, padding: 12, background: "#f5f5f5", borderRadius: 4 }}>
                                        <div><strong>前一航班:</strong> {editingMaintenance.prev_flight_number} ({editingMaintenance.prev_flight_date})</div>
                                        <div><strong>后一航班:</strong> {editingMaintenance.next_flight_number} ({editingMaintenance.next_flight_date})</div>
                                    </div>
                                );
                            }

                            if (status === "CONFIRMED" && !alreadyHasFlights) {
                                return (
                                    <>
                                        <Divider orientation="left" plain style={{ margin: "12px 0" }}>
                                            选择维护区间航班 <span style={{ fontSize: 12, color: "#999" }}>(UTC+8)</span>
                                        </Divider>
                                        <Form.Item
                                            name="prev_flight_key"
                                            label="前一航班（维护开始锚点）"
                                            rules={[{ required: true, message: "请选择前一航班" }]}
                                        >
                                            <Select
                                                showSearch
                                                placeholder="选择前一航班"
                                                loading={editFlightsLoading}
                                                disabled={editFlights.length < 2}
                                                onChange={handleEditPrevFlightChange}
                                                filterOption={(input, option) =>
                                                    option.children?.toString().toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {editFlights.slice(0, -1).map(renderEditFlightOption)}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            name="next_flight_key"
                                            label="后一航班（维护结束锚点）"
                                            rules={[{ required: true, message: "请选择后一航班" }]}
                                        >
                                            <Select
                                                showSearch
                                                placeholder={editPrevFlightIndex >= 0 ? "可调整后一航班" : "请先选择前一航班"}
                                                loading={editFlightsLoading}
                                                disabled={editPrevFlightIndex < 0}
                                                filterOption={(input, option) =>
                                                    option.children?.toString().toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {editPrevFlightIndex >= 0
                                                    ? editFlights.slice(editPrevFlightIndex + 1).map(renderEditFlightOption)
                                                    : []}
                                            </Select>
                                        </Form.Item>
                                    </>
                                );
                            }

                            return null;
                        }}
                    </Form.Item>

                    <Form.Item
                        name="event_type"
                        label="维护类型"
                        rules={[
                            { required: true, message: "请选择维护类型" },
                        ]}
                    >
                        <Select placeholder="选择维护类型">
                            <Option value="SCHEDULED">定检维护</Option>
                            <Option value="ROUTINE">例行维护</Option>
                            <Option value="AD_HOC">临时维护</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="title"
                        label="显示标题"
                    >
                        <Input
                            placeholder="例如：定检维护 (24h)"
                            maxLength={100}
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="备注（可选）"
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="备注信息"
                            maxLength={500}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ScreenControlPage;
